import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import WizrText from '../WizrText';
import { CloseCircleOutlined, DownOutlined } from '@ant-design/icons';
import WizrFlexLayout from '../WizrFlexLayout';
import { Dropdown } from 'antd';

const TagsContainer = styled(WizrFlexLayout)`
  gap: 8px;
  flex-wrap: wrap;
  min-height: 32px;
  padding: 4px 0;
  justify-content: flex-start;
  width: 100%;
`;

const FilterTag = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background: #FFFFFF;
  border-radius: 16px;
  border: 1px solid #E5E7EB;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #F9FAFB;
    border-color: #D1D5DB;
  }
`;

const MoreFiltersTag = styled(FilterTag)`
  background: #F3F4F6;
  cursor: pointer;
  
  &:hover {
    background: #E5E7EB;
    border-color: #D1D5DB;
  }
`;

const ClearAllButton = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 8px;
  color: #6B7280;
  font-size: 11px;
  cursor: pointer;
  margin-left: auto;
  white-space: nowrap;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  text-decoration: underline;

  &:hover {
    color: #4B5563;
  }
`;

interface FilterTagsProps {
  filters: { [key: string]: { values: string[]; columnName: string } };
  onFilterRemove?: (key: string, value: string) => void;
  onClearAll?: () => void;
  filterData?: any; // Add filterData prop to access bot mapping
}

const formatFilterValue = (key: string, value: string, filterData?: any): string => {
  // Check if the value is a date string (ISO format)
  if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
    // Check if it's a date range (contains : separator)
    if (value.includes(' : ')) {
      const [startDate, endDate] = value.split(' : ');
      try {
        const formattedStartDate = new Date(startDate).toLocaleDateString();
        const formattedEndDate = new Date(endDate).toLocaleDateString();
        return `${formattedStartDate} - ${formattedEndDate}`;
      } catch {
        return value;
      }
    } else if (value.includes('T')) {
      try {
        const date = new Date(value);
        return date.toLocaleDateString();
      } catch {
        return value;
      }
    }
  }
  
  // Handle bot name mapping - if this is a bot_name filter and we have Bot_name data
  if (key.toLowerCase() === 'bot_name' && filterData?.data?.Bot_name) {
    const botMapping = filterData.data.Bot_name;
    if (botMapping[value]) {
      return botMapping[value];
    }
  }
  
  return value;
};

const FilterTags: React.FC<FilterTagsProps> = ({ filters, onFilterRemove, onClearAll, filterData }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [firstRowFilters, setFirstRowFilters] = useState<Array<{ key: string; value: string; columnName: string }>>([]);
  const [remainingFilters, setRemainingFilters] = useState<Array<{ key: string; value: string; columnName: string }>>([]);

  // Handle clear all filters
  const handleClearAll = () => {
    // Reset local state first
    setFirstRowFilters([]);
    setRemainingFilters([]);
    setDropdownVisible(false);
    
    // Call parent's onClearAll if provided
    if (onClearAll) {
      onClearAll();
    }
  };

  useEffect(() => {
    

    if (!filters || Object.keys(filters).length === 0) {
     
      setFirstRowFilters([]);
      setRemainingFilters([]);
      return;
    }

   console.log('3b. Processing filters, Object.entries result:', Object.entries(filters));

    // Convert filters to flat array with proper column names
    try {
      const allFilters = Object.entries(filters).reduce((acc, [key, filter]) => {
       
        
        if (!filter?.values) {
          
          return acc;
        }

        try {
          filter.values.forEach(value => {
          
            acc.push({
              key,
              value,
              columnName: filter.columnName ?? key
            });
          });
        } catch (valueError) {
          console.error('Error processing values:', valueError);
        }
        return acc;
      }, [] as Array<{ key: string; value: string; columnName: string }>);

      

     
      if (containerRef.current) {
       
        const container = containerRef.current;
        const containerWidth = container.offsetWidth;
        let currentWidth = 0;
        const firstRow: typeof allFilters = [];
        const remaining: typeof allFilters = [];

         
        if (allFilters.length === 0) {
          console.log('6d. Warning: allFilters array is empty');
        }

        allFilters.forEach((filter, index) => {
          
          try {
            const tempTag = document.createElement('div');
            tempTag.style.cssText = 'position: absolute; visibility: hidden;';
            const displayText = `${filter.columnName}: ${formatFilterValue(filter.key, filter.value, filterData)}`;
            
            tempTag.innerHTML = displayText;
            container.appendChild(tempTag);
            const tagWidth = tempTag.offsetWidth + 50;
            container.removeChild(tempTag);
          
            if (currentWidth + tagWidth <= containerWidth - 100) {
             
              firstRow.push(filter);
              currentWidth += tagWidth;
            } else {
              
              remaining.push(filter);
            }
          } catch (error) {
            console.error('Error measuring filter:', error);
          }
        });

       

        setFirstRowFilters(firstRow);
        setRemainingFilters(remaining);
      } else {
        console.error('6x. Container ref is null - width calculation skipped');
      }
    } catch (error) {
      console.error('Error processing filters:', error);
    }
  }, [filters]);

  

  if (!filters || Object.keys(filters ?? {}).length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <TagsContainer style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', paddingRight: '70px' }}>
        <>
          {firstRowFilters.map((filter) => (
            <FilterTag key={`${filter.key}-${filter.value}`}>
              <WizrText type="sub2" style={{ fontSize: '11px', color: '#4B5563' }}>
                {`${filter.columnName}: ${formatFilterValue(filter.key, filter.value, filterData)}`}
              </WizrText>
              {onFilterRemove && (
                <CloseCircleOutlined
                  style={{ fontSize: '10px', color: '#9CA3AF' }}
                  onClick={() => onFilterRemove(filter.key, filter.value)}
                />
              )}
            </FilterTag>
          ))}
          
          {remainingFilters.length > 0 && (
            <Dropdown 
              menu={{ items: remainingFilters.map((filter) => ({
                key: `${filter.key}-${filter.value}`,
                label: (
                  <FilterTag>
                    <WizrText type="sub2" style={{ fontSize: '11px', color: '#4B5563' }}>
                      {`${filter.columnName}: ${formatFilterValue(filter.key, filter.value, filterData)}`}
                    </WizrText>
                    {onFilterRemove && (
                      <CloseCircleOutlined
                        style={{ fontSize: '10px', color: '#9CA3AF' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onFilterRemove(filter.key, filter.value);
                        }}
                      />
                    )}
                  </FilterTag>
                )
              })) }}
              open={dropdownVisible}
              onOpenChange={setDropdownVisible}
              trigger={['click']}
            >
              <MoreFiltersTag onClick={() => setDropdownVisible(!dropdownVisible)}>
                <WizrText type="sub2" style={{ fontSize: '11px', color: '#4B5563' }}>
                  +{remainingFilters.length} more
                </WizrText>
                <DownOutlined style={{ fontSize: '10px', color: '#9CA3AF' }} />
              </MoreFiltersTag>
            </Dropdown>
          )}
        </>
      </TagsContainer>

      {(firstRowFilters.length > 0 || remainingFilters.length > 0) && (
        <ClearAllButton onClick={handleClearAll}>
          <WizrText type="sub2" style={{ fontSize: '11px' }}>
            Clear all
          </WizrText>
        </ClearAllButton>
      )}
    </div>
  );
};

export default FilterTags; 